import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MultiOptionsDataType,
} from "../../../../components";
import { MuiLink } from "../../../../components/link/MuiLink";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  siteCoreContentLoadingSelector,
  userPreferredLanguageSelector,
} from "../../../../redux/slices/appState/appStateSelector";
import { createProgramSiteCoreKeys } from "../../../createProgramTemplate/siteCore/SiteCoreKeys";
import { ProgramTemplateData } from "../../modelsAndSchemas/Models";
import { ViewProgramTemplateProfileWrapper } from "./ViewProgramTemplateProfile.style";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";

interface ViewProgramTemplateProfileProps {
  programTemplateData: ProgramTemplateData;
  pageId: string;
  setShowLocaleModal: () => void;
}

interface ProfileFieldProps {
  label: string;
  value: string | number | undefined;
  showLocaleLink?: boolean;
  showStatusBadge?: boolean;
}

const ViewProgramTemplateProfile: FC<ViewProgramTemplateProfileProps> = (
  props,
) => {
  const { programTemplateData, pageId, setShowLocaleModal } = props;
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const gridRef = useRef<HTMLDivElement>(null); // Create a reference for the MuiGrid
  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const statusLabel = fetchValueByKeyOfDropdown(
    createProgramSiteCoreKeys.profileSection,
    createProgramSiteCoreKeys.statusList,
    programTemplateData?.status,
  );

  const programName =
    programTemplateData?.localizedProgramTemplateNames?.find(
      (item) => item.localekey === userSelectedLanguage,
    )?.name ?? programTemplateData?.name;

  const profileFields = useMemo<ProfileFieldProps[]>(() => {
    if (!programTemplateData || siteCoreLoading) return [];

    return [
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.nameLabel,
        ),
        value: programName,
        showLocaleLink: true,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.programFrameworkLabel,
        ),
        value: programTemplateData?.programFramework,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.nextViewLabel,
        ),
        value: programTemplateData?.solutionOfferings,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.productLine,
        ),
        value: programTemplateData?.lineOfBusiness,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.solutionCategoryLabel,
        ),
        value: programTemplateData?.solutionCategory,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.solutionSubCategory,
        ),
        value: programTemplateData?.solutionCategoryLob,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.nextProgramClassLabel,
        ),
        value: programTemplateData?.programClass,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.durationMonthsLabel,
        ),
        value: String(programTemplateData?.durationInMonths),
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.closingReasonType,
        ),
        value: fetchValueByKeyOfDropdown(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.closingReasonTypeList,
          programTemplateData?.closingReason,
        ),
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.statusLabel,
        ),
        value: "",
        showStatusBadge: true,
      },
      {
        label: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.nextAccessLabel,
        ),
        value: programTemplateData?.nextAccess
          ? fetchValueByKeyOfDropdown(
              createProgramSiteCoreKeys.profileSection,
              createProgramSiteCoreKeys.nextAccessList,
              createProgramSiteCoreKeys?.nextAccessYes,
            )
          : fetchValueByKeyOfDropdown(
              createProgramSiteCoreKeys.profileSection,
              createProgramSiteCoreKeys.nextAccessList,
              createProgramSiteCoreKeys?.nextAccessNo,
            ),
      },
    ];
  }, [programTemplateData, programName, siteCoreLoading]);

  return (
    <ViewProgramTemplateProfileWrapper>
      <MuiGrid container className="main-container">
        {profileFields.map((field) => (
          <MuiGrid
            size={gridSize}
            key={field.label}
            className={gridSizeMediumClass}
          >
            <MuiBox>
              <MuiDoubleLabel
                label={field.label}
                value={field.value ?? ""}
                replaceEmptyWithDash={!field.showStatusBadge}
                isValueWithEllipsis
              />

              {field.showStatusBadge && (
                <StatusBadge
                  status={programTemplateData?.status as StatusBadgeType}
                  label={statusLabel}
                />
              )}
              {field.showLocaleLink && (
                <MuiLink
                  className="view-locale-link"
                  onClick={setShowLocaleModal}
                  id={`${pageId}-locale`}
                  data-testid={`${pageId}-locale`}
                >
                  {commonLabels.viewLocale}
                </MuiLink>
              )}
            </MuiBox>
          </MuiGrid>
        ))}
      </MuiGrid>
    </ViewProgramTemplateProfileWrapper>
  );
};

export default ViewProgramTemplateProfile;
